import { useCallback, useRef, useState } from 'react';

import { DEFAULT_COLOR_PALETTE } from '@/lib/constants/colors';
import { ProjectIntegrationsFormData } from '@/modules/projects';
import { ProjectStatus } from '@/modules/projects/types/project';

import { ProjectFormData, ProjectTab } from '../types';

export interface ProjectDraft {
  info?: Partial<ProjectFormData>;
  billing?: Partial<ProjectFormData>;
  integrations?: Partial<ProjectIntegrationsFormData>;
}

interface DraftOperationParams<T> {
  tab: ProjectTab;
  data: Partial<T>;
}

interface UseProjectDraftReturn<T> {
  draft: ProjectDraft;
  saveDraftData: (params: DraftOperationParams<T>) => void;
  getDraftData: (tab: ProjectTab) => Partial<T> | undefined;
  clearDraft: () => void;
  hasDraftData: boolean;
  getFinalSubmissionData: () => ProjectFormData;
}

/**
 * Manages project draft state across multiple modes (Create, Edit, Read) and tabs.
 * Provides a persistent form of data storage during the project workflow.
 *
 * Features:
 * - Tab-based data persistence for project info, billing, and integrations
 * - Automatic state synchronization between React state and refs for immediate access
 * - Draft data validation and cleanup utilities
 * - Final submission data aggregation from all tabs
 * - Works in Create, Edit, and Read modes
 *
 * @template T - The type of data being managed by the hook
 */
export function useProjectDraft<T = ProjectFormData>(): UseProjectDraftReturn<T> {
  const [draft, setDraft] = useState<ProjectDraft>({});
  const draftRef = useRef<ProjectDraft>({});

  draftRef.current = draft;

  const saveDraftData = useCallback(({ tab, data }: DraftOperationParams<T>) => {
    setDraft(prevDraft => ({
      ...prevDraft,
      [tab]: {
        ...prevDraft[tab],
        ...data,
      },
    }));
  }, []);

  const getDraftData = useCallback((tab: ProjectTab): Partial<T> | undefined => {
    return draftRef.current[tab] as Partial<T> | undefined;
  }, []);

  const clearDraft = useCallback(() => {
    setDraft({});
  }, []);

  const hasDraftData = Object.keys(draft).some(tab => {
    const tabData = draft[tab as ProjectTab];
    return tabData && Object.keys(tabData).length > 0;
  });

  const getFinalSubmissionData = useCallback((): ProjectFormData => {
    // Use draftRef.current to get the latest draft data (includes pending state updates)
    const currentDraft = draftRef.current;
    const infoData = currentDraft.info || {};
    const billingData = currentDraft.billing || {};
    const integrationData = currentDraft.integrations || {};

    return {
      // Info data
      name: infoData.name || '',
      color: infoData.color || DEFAULT_COLOR_PALETTE.pink,
      logo: infoData.logo,
      status: infoData.status || ProjectStatus.Active,
      probability: infoData.probability,
      industryIds: infoData.industryIds || [],
      clientId: infoData.clientId,
      pendingClientName: infoData.pendingClientName,
      managerIds: infoData.managerIds,
      startDate: infoData.startDate,
      description: infoData.description || '',
      customAttributes: infoData.customAttributes || {},

      // Billing data
      billingModel: billingData.billingModel,
      currency: billingData.currency,
      rateSource: billingData.rateSource,
      flatRate: billingData.flatRate,
      projectBudget: billingData.projectBudget,
      selectedTeamMembers: billingData.selectedTeamMembers,
      cachedTeamMembers: billingData.cachedTeamMembers || [],
      defaultRateSource: billingData.defaultRateSource,

      // Integration data
      integrations: integrationData.integrations || [],
    };
  }, []);

  return {
    draft,
    saveDraftData,
    getDraftData,
    clearDraft,
    hasDraftData,
    getFinalSubmissionData,
  };
}
